import { useContext } from "react";

import CircleCheck from "@svgs/circle-check.svg";
import CircleClose from "@svgs/close-round-thick.svg";
import { cn } from "@helpers/general";
import { getLuxeGiftInfo } from "@helpers/luxe";
import { useAuth } from "@context/AuthProvider";
import { LuxeContext } from "@context/LuxeProvider";

// Icon centers for a 3-item flex-1 layout: 1/6, 3/6, 5/6
const MILESTONE_ANCHORS = { m1: 100 / 6, m2: 50, m3: 500 / 6 };

/**
 * getVisualProgressPercent
 * ------------------------------------------------------------
 * Maps the user's progress (in decimal months) to a visual percentage
 * based on the actual position of milestone markers in the design.
 *
 * This ensures the progress bar fills precisely to each marker
 * as the user crosses month boundaries, even if markers are unevenly spaced.
 *
 */
function getVisualProgressPercent(exactMonths, anchors) {
  const markers = [0, anchors.m1, anchors.m2, anchors.m3, 100];

  const segment = Math.min(3, Math.floor(exactMonths)); // 0, 1, 2, or 3
  const segmentFraction = exactMonths - segment;

  const segmentStart = markers[segment];
  const segmentEnd = markers[segment + 1];

  return segmentStart + (segmentEnd - segmentStart) * segmentFraction;
}

function LuxeGiftEligibilityProgressBar() {
  const { accountDetails } = useAuth();
  const { luxe } = useContext(LuxeContext);
  const timeline = getLuxeGiftInfo(luxe, accountDetails?.tags);

  if (!timeline) return null;

  const { exactMonths, isMonth1Complete, isGiftUnlocked, isCountdownStarted, expiryMonthLabel } =
    timeline;

  const visualProgress = getVisualProgressPercent(exactMonths, MILESTONE_ANCHORS);

  const steps = [
    {
      key: "m1",
      completed: isMonth1Complete,
      title: "Month 1 Charge",
      subtitle: "First Month Complete",
    },
    {
      key: "m2",
      completed: isGiftUnlocked,
      title: "Month 2 Charge",
      subtitle: "Gift Unlocked",
    },
    {
      key: "m3",
      completed: isCountdownStarted,
      title: "Month 3 Charge",
      subtitle: `Gift Expires ${expiryMonthLabel}`,
    },
  ];

  return (
    <div className="bg-white rounded-[4px] p-[15px] pt-[20px] desktop:pb-[20px] desktop:pt-[25px] desktop:px-[25px] desktop:flex desktop:flex-1 desktop:items-center">
      <div className="relative desktop:w-full">
        <div className="relative flex items-center">
          <div className="absolute left-0 right-0 h-[8px] rounded-[4px]">
            <div className="absolute inset-0 bg-light-grey rounded-[4px]" />
            <div
              className="absolute left-0 top-0 h-full bg-black rounded-[4px] transition-[width] duration-300 ease-out max-w-full"
              style={{ width: `${visualProgress}%` }}
            />
          </div>
          <div className="relative z-[1] flex w-full">
            {steps.map(({ key, completed }) => (
              <div key={key} className="flex-1 flex justify-center">
                {key === "m3" ? (
                  <CircleClose
                    className={cn(
                      "h-[23px] w-[23px] desktop:h-[28px] desktop:w-[28px]",
                      completed
                        ? "[&_path:first-child]:fill-black [&_path:first-child]:stroke-black [&_path:not(:first-child)]:stroke-white"
                        : "[&_circle]:stroke-dark-grey [&_path]:stroke-dark-grey fill-white"
                    )}
                  />
                ) : (
                  <CircleCheck
                    className={cn(
                      "h-[23px] w-[23px] desktop:h-[28px] desktop:w-[28px]",
                      completed
                        ? "[&_circle]:stroke-black [&_path]:stroke-white fill-black"
                        : "[&_circle]:stroke-dark-grey [&_path]:stroke-dark-grey fill-white"
                    )}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="flex mt-[10px] desktop:mt-[15px]">
          {steps.map(({ key, title, subtitle }) => (
            <div key={key} className="flex-1 flex flex-col items-center text-center">
              <p className="typo-body-bold">{title}</p>
              <p className="typo-body">{subtitle}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default LuxeGiftEligibilityProgressBar;
